import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  LocationOn as LocationOnIcon,
  Delete as DeleteIcon,
  SwapVert as SwapVertIcon,
  Search as SearchIcon,
  ZoomInMap as ZoomInMapIcon,
  Directions as DirectionsIcon,
  Straighten as StraightenIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import { transformExtent } from "ol/proj";
import {
  geocodeAddress,
  reverseGeocode,
  fetchRoute,
  formatDistance,
  formatDuration,
  stepInstruction,
} from "./directionApi";

const BLUE = "#1976d2";

/** Stop badge colour: start green, end red, middle blue (map markers se match) */
function stopColor(index, total) {
  if (index === 0) return "#4caf50";
  if (index === total - 1) return "#f44336";
  return BLUE;
}

/** direction tool drawer panel */
export default function DirectionToolPanel(ctx) {
  const {
    mapInstanceRef,
    directionStops,
    setDirectionStops,
    directionRoute,
    setDirectionRoute,
    directionLoading,
    setDirectionLoading,
    directionError,
    setDirectionError,
    directionPickingId,
    setDirectionPickingId,
    directionPickRef,
    directionActiveStep,
    zoomToDirectionStep,
    fitDirectionRoute,
    clearDirection,
    onCloseTool,
  } = ctx;

  // Geocode results per stop (panel-local: tab switch par reset hona theek hai)
  const [results, setResults] = useState({});
  const [searching, setSearching] = useState({});

  const placedCount = directionStops.filter(
    (s) => s.lon != null && s.lat != null,
  ).length;

  const updateStop = (id, patch) =>
    setDirectionStops((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    );

  /* ------------------------- geocode (Enter / 🔍) ------------------------ */
  const runSearch = async (stop) => {
    const q = (stop.query || "").trim();
    if (!q) return;
    setSearching((p) => ({ ...p, [stop.id]: true }));
    setDirectionError("");
    try {
      // current map extent ke hisaab se results bias karo (nearby pehle)
      let viewbox = null;
      const map = mapInstanceRef.current;
      if (map) {
        const ext = map.getView().calculateExtent(map.getSize());
        viewbox = transformExtent(ext, "EPSG:3857", "EPSG:4326");
      }
      const found = await geocodeAddress(q, viewbox);
      setResults((p) => ({ ...p, [stop.id]: found }));
      if (found.length === 0) setDirectionError(`No results for "${q}"`);
    } catch (e) {
      setDirectionError(e.message || "Geocoding failed");
    } finally {
      setSearching((p) => ({ ...p, [stop.id]: false }));
    }
  };

  const chooseResult = (stop, r) => {
    updateStop(stop.id, { query: r.label, label: r.label, lon: r.lon, lat: r.lat });
    setResults((p) => ({ ...p, [stop.id]: [] }));
    setDirectionRoute(null);
  };

  /* --------------------------- pick on map ------------------------------ */
  const startPick = (stop) => {
    if (directionPickingId === stop.id) {
      // dobara click = cancel
      directionPickRef.current = null;
      setDirectionPickingId(null);
      return;
    }
    setDirectionPickingId(stop.id);
    // Map click handler (OpenLayer.js) is callback ko ek baar call karta hai
    directionPickRef.current = async (lon, lat) => {
      setDirectionPickingId(null);
      updateStop(stop.id, {
        lon,
        lat,
        label: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
        query: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
      });
      setResults((p) => ({ ...p, [stop.id]: [] }));
      setDirectionRoute(null);
      const name = await reverseGeocode(lon, lat);
      updateStop(stop.id, { label: name, query: name });
    };
  };

  /* --------------------------- stop list ops ---------------------------- */
  const addStop = () =>
    setDirectionStops((prev) => [
      ...prev,
      { id: Date.now(), query: "", label: "", lon: null, lat: null },
    ]);

  const removeStop = (id) => {
    if (directionStops.length <= 2) return;
    setDirectionStops((prev) => prev.filter((s) => s.id !== id));
    setDirectionRoute(null);
  };

  const reverseStops = () => {
    setDirectionStops((prev) => [...prev].reverse());
    setDirectionRoute(null);
  };

  /* ------------------------------ route --------------------------------- */
  const getRoute = async () => {
    setDirectionError("");
    setDirectionLoading(true);
    try {
      const route = await fetchRoute(directionStops);
      setDirectionRoute(route);
      // route aane ke baad map fit karo (state update ke baad, next tick)
      setTimeout(() => fitDirectionRoute(route), 0);
    } catch (e) {
      setDirectionRoute(null);
      setDirectionError(e.message || "Routing failed");
    } finally {
      setDirectionLoading(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 600, color: BLUE }}
      >
        Directions
      </Typography>

      {/* Stops */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>
          Stops (search an address or pick on map)
        </Typography>
        <Tooltip title="Reverse order">
          <span>
            <IconButton
              size="small"
              onClick={reverseStops}
              disabled={placedCount < 2}
              sx={{ color: BLUE, p: 0.3 }}
            >
              <SwapVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ maxHeight: 260, overflow: "auto", pr: 0.5 }}>
        {directionStops.map((stop, idx) => {
          const color = stopColor(idx, directionStops.length);
          const picking = directionPickingId === stop.id;
          const stopResults = results[stop.id] || [];
          return (
            <Box key={stop.id} sx={{ mb: 1 }}>
              <Box sx={{ display: "flex", gap: 0.7, alignItems: "center" }}>
                {/* number badge */}
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: color,
                    color: "white",
                    fontSize: 11,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </Box>

                <TextField
                  size="small"
                  fullWidth
                  placeholder={
                    idx === 0
                      ? "Start: address or place"
                      : idx === directionStops.length - 1
                        ? "Destination"
                        : "Via"
                  }
                  value={stop.query}
                  onChange={(e) =>
                    updateStop(stop.id, { query: e.target.value })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") runSearch(stop);
                  }}
                  sx={{ bgcolor: "white" }}
                  InputProps={{
                    sx: { fontSize: 12, pr: 0.5 },
                    endAdornment: searching[stop.id] ? (
                      <CircularProgress size={14} />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => runSearch(stop)}
                        title="Search address"
                        sx={{ p: 0.3, color: BLUE }}
                      >
                        <SearchIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    ),
                  }}
                />

                {/* pick on map — same look as Asset Search point button */}
                <IconButton
                  onClick={() => startPick(stop)}
                  title="Pick this stop on the map"
                  sx={{
                    bgcolor: BLUE,
                    color: "white",
                    borderRadius: 1,
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    border: picking ? "2px solid #000" : "2px solid transparent",
                    "&:hover": { bgcolor: "#2d9905" },
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: 18 }} />
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() => removeStop(stop.id)}
                  disabled={directionStops.length <= 2}
                  title="Remove stop"
                  sx={{ color: "#f44336", p: 0.3, flexShrink: 0 }}
                >
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {stop.lon != null && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 10.5,
                    color: "#4caf50",
                    fontWeight: 600,
                    display: "block",
                    pl: 3.7,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={stop.label}
                >
                  ✓ {stop.label}
                </Typography>
              )}

              {stopResults.length > 0 && (
                <List
                  dense
                  disablePadding
                  sx={{
                    ml: 3.7,
                    mt: 0.5,
                    border: `1px solid ${BLUE}`,
                    borderRadius: 1,
                    bgcolor: "white",
                    maxHeight: 150,
                    overflow: "auto",
                  }}
                >
                  {stopResults.map((r, i) => (
                    <ListItemButton
                      key={i}
                      onClick={() => chooseResult(stop, r)}
                      sx={{ py: 0.4, "&:hover": { bgcolor: "#e3f2fd" } }}
                    >
                      <ListItemText
                        primary={r.label}
                        primaryTypographyProps={{ fontSize: 11 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          );
        })}
      </Box>

      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addStop}
        sx={{ borderColor: BLUE, color: BLUE, mb: 1.5, alignSelf: "flex-start" }}
      >
        Add stop
      </Button>

      {directionPickingId != null && (
        <Box
          sx={{
            mb: 1.5,
            p: 1,
            bgcolor: "#fff3e0",
            borderRadius: 1,
            border: `1px solid ${BLUE}`,
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 11, color: "#e65100" }}>
            🖱️ Click on the map to set stop{" "}
            {directionStops.findIndex((s) => s.id === directionPickingId) + 1}
          </Typography>
        </Box>
      )}

      {directionError && (
        <Typography
          variant="caption"
          sx={{ fontSize: 11, color: "#f44336", mb: 1, display: "block" }}
        >
          {directionError}
        </Typography>
      )}

      <Divider sx={{ mb: 1 }} />

      {/* Summary + narrative */}
      <Box sx={{ flex: 1, overflow: "auto", minHeight: 80 }}>
        {directionLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
            <CircularProgress />
          </Box>
        ) : !directionRoute ? (
          <Typography variant="caption" sx={{ color: "#999", fontSize: 11 }}>
            {placedCount < 2
              ? "Set at least 2 stops, then press Get Route"
              : "Press Get Route"}
          </Typography>
        ) : (
          <>
            {/* summary */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 0.8,
                px: 1,
                mb: 1,
                bgcolor: "#f0f4fa",
                borderRadius: 1,
              }}
            >
              <DirectionsIcon sx={{ fontSize: 18, color: BLUE }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                <StraightenIcon sx={{ fontSize: 14, color: "#666" }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: BLUE }}>
                  {formatDistance(directionRoute.distance)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                <AccessTimeIcon sx={{ fontSize: 14, color: "#666" }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: BLUE }}>
                  {formatDuration(directionRoute.duration)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Zoom to route">
                <IconButton
                  size="small"
                  onClick={() => fitDirectionRoute(directionRoute)}
                  sx={{ color: BLUE, p: 0.3 }}
                >
                  <ZoomInMapIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* narrative */}
            {directionRoute.legs.map((leg, li) => {
              const placed = directionStops.filter(
                (s) => s.lon != null && s.lat != null,
              );
              const from = placed[li]?.label || `Stop ${li + 1}`;
              const to = placed[li + 1]?.label || `Stop ${li + 2}`;
              const isLastLeg = li === directionRoute.legs.length - 1;
              return (
                <Box key={li} sx={{ mb: 1 }}>
                  {directionRoute.legs.length > 1 && (
                    <Box
                      sx={{
                        py: 0.6,
                        px: 1,
                        bgcolor: "#f0f4fa",
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 11.5, fontWeight: 600, color: BLUE }}
                        noWrap
                        title={`${from} → ${to}`}
                      >
                        Leg {li + 1}: {formatDistance(leg.distance)} ·{" "}
                        {formatDuration(leg.duration)}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 10.5, color: "#666" }}
                        noWrap
                        title={`${from} → ${to}`}
                      >
                        {from} → {to}
                      </Typography>
                    </Box>
                  )}
                  {leg.steps.map((step, si) => {
                    const active =
                      directionActiveStep &&
                      directionActiveStep.leg === li &&
                      directionActiveStep.step === si;
                    const text = stepInstruction(step, {
                      isFinalArrive: isLastLeg,
                      stopNumber: li + 2,
                    });
                    return (
                      <Box
                        key={si}
                        onClick={() => zoomToDirectionStep(li, si)}
                        sx={{
                          display: "flex",
                          gap: 1,
                          px: 1,
                          py: 0.6,
                          cursor: "pointer",
                          borderRadius: 1,
                          borderLeft: `3px solid ${active ? "#ff9800" : "transparent"}`,
                          bgcolor: active ? "#fff3e0" : "transparent",
                          "&:hover": { bgcolor: active ? "#fff3e0" : "#f5f5f5" },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: "#999",
                            width: 18,
                            flexShrink: 0,
                            pt: 0.2,
                          }}
                        >
                          {si + 1}.
                        </Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12, lineHeight: 1.35 }}>
                            {text}
                          </Typography>
                          {step.distance > 0 && (
                            <Typography sx={{ fontSize: 10.5, color: "#888" }}>
                              {formatDistance(step.distance)}
                              {step.duration > 30
                                ? ` · ${formatDuration(step.duration)}`
                                : ""}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
            <Typography
              variant="caption"
              sx={{ fontSize: 9.5, color: "#aaa", display: "block", mt: 1 }}
            >
              Routing © OSRM · Geocoding © OpenStreetMap contributors
            </Typography>
          </>
        )}
      </Box>

      <Divider sx={{ mt: 1, mb: 1 }} />
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          fullWidth
          size="small"
          variant="contained"
          onClick={getRoute}
          disabled={placedCount < 2 || directionLoading}
          sx={{ bgcolor: BLUE, "&:hover": { bgcolor: "#115293" } }}
        >
          {directionLoading ? (
            <CircularProgress size={18} sx={{ color: "white" }} />
          ) : (
            "Get Route"
          )}
        </Button>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={() => {
            clearDirection();
            setResults({});
          }}
          sx={{ borderColor: BLUE, color: BLUE }}
        >
          Clear
        </Button>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<CloseIcon />}
          onClick={() => {
            clearDirection();
            onCloseTool("direction");
          }}
          sx={{
            borderColor: "#f44336",
            color: "#f44336",
            "&:hover": { bgcolor: "#ffebee" },
          }}
        >
          Close
        </Button>
      </Box>
    </Box>
  );
}
